import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { IconButton } from './IconButton';
import { Button } from './Button';
import { Portal } from './Portal';
import { toastReducer, resolveToastDuration, isToastPaused, MAX_HOLD_MS, type Toast, type ToastOptions, type ToastVariant } from './toastState.ts';
import './Toast.css';

export { toastReducer, resolveToastDuration, isToastPaused, MAX_HOLD_MS };
export type { Toast, ToastOptions, ToastAction, ToastVariant } from './toastState.ts';

const ICONS: Record<ToastVariant, IconName> = {
  success: 'check',
  info: 'info',
  error: 'alert',
};

let toastSeq = 0;
function nextToastId(): string {
  toastSeq += 1;
  return `toast-${toastSeq}`;
}

interface ToastContextValue {
  show: (opts: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** DESIGN.md §9.1 `Toast`/`ToastAction`. Mounted ONLY in the KJ app tree (`App.tsx`'s
 * `ConsoleShell`) — the Stage view must never render anything from the App (DESIGN.md), and the
 * guest page has no operator-facing transient status of this kind. */
export function ToastProvider({
  children,
  dismissLabel,
}: {
  children: ReactNode;
  /** Every toast's dismiss button shares one accessible name. `ui/` stays dictionary-free (see
   * `ui/SearchField.tsx`'s rule), so `App.tsx` — the only mount point, see this file's own doc
   * comment — passes `t('common.dismiss')`.
   *
   * MVP-357: required. The English `?? 'Dismiss'` default it replaced could only ever have been read
   * out by a screen reader on a Thai screen, since the one mount point has always passed a value. */
  dismissLabel: string;
}) {
  const [toasts, dispatch] = useReducer(toastReducer, [] as Toast[]);

  const dismiss = useCallback((id: string) => dispatch({ type: 'dismiss', id }), []);

  const show = useCallback((opts: ToastOptions): string => {
    const id = nextToastId();
    const durationMs = resolveToastDuration(opts.variant, opts.durationMs);
    dispatch({ type: 'add', toast: { ...opts, id, durationMs } });
    return id;
  }, []);

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Portal>
        <div className="toast-region">
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} dismissLabel={dismissLabel} />
          ))}
        </div>
      </Portal>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

function ToastItem({
  toast,
  onDismiss,
  dismissLabel,
}: {
  toast: Toast;
  onDismiss: () => void;
  dismissLabel: string;
}) {
  const [leaving, setLeaving] = useState(false);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const requestClose = useCallback(() => setLeaving(true), []);

  /**
   * MVP-381. A toast carrying an action — the cut receipt's Undo, above all — is an interactive
   * control on a countdown, which WCAG 2.2.1 asks to be pausable. Two things stop the clock:
   *
   * - the pointer resting on the toast, or focus landing anywhere inside it. That is the KJ in the
   *   act of reaching for Undo; taking it away mid-reach is the exact failure.
   * - the document being hidden. This is a dual-screen console: the KJ switches to the Stage window
   *   or another tab routinely, and a receipt that burned down while nobody could see it was never
   *   offered at all.
   *
   * ponytail: unpausing restarts the full duration rather than resuming the remainder. It only ever
   * gives the operator more time, never less, and it costs one state variable instead of a
   * timestamp/remaining-ms pair. Swap in remaining-time tracking if a toast ever needs to honour an
   * exact total on-screen budget.
   */
  const [held, setHeld] = useState(false);
  const [holdExpired, setHoldExpired] = useState(false);
  const [documentHidden, setDocumentHidden] = useState(() => typeof document !== 'undefined' && document.hidden);

  useEffect(() => {
    const sync = () => setDocumentHidden(document.hidden);
    document.addEventListener('visibilitychange', sync);
    sync();
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  /**
   * MVP-409. The pause MVP-381 added had no ceiling, and the toast floats over live controls:
   * reaching for one of them puts the pointer on the toast, which held the clock, which kept the
   * toast over the control. The gesture meant to get past the toast was the one that made it
   * permanent, and the only exit left was the dismiss button. `.toast-region`'s width is the other
   * half of the same fix (Toast.css); `isToastPaused` in toastState.ts is the rule itself.
   *
   * Resets the moment the pointer leaves, so the next reach gets its own full budget rather than
   * inheriting a spent one. When it expires the toast is simply no longer held and the ordinary
   * duration runs from there — the operator still gets all of it, they just cannot buy an unbounded
   * extension by leaving a pointer parked.
   */
  useEffect(() => {
    if (!held) {
      setHoldExpired(false);
      return;
    }
    const timer = setTimeout(() => setHoldExpired(true), MAX_HOLD_MS);
    return () => clearTimeout(timer);
  }, [held]);

  const paused = isToastPaused({ held, holdExpired, documentHidden });

  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(requestClose, toast.durationMs);
    return () => clearTimeout(timer);
  }, [toast.durationMs, requestClose, paused]);

  // Actually removes the toast once it starts leaving — driven by `onTransitionEnd` when the CSS
  // exit transition (MVP-261) finishes, with this timeout as the fallback for `prefers-reduced-motion`
  // (base.css clamps the transition to 1ms there, so the event still fires in practice, but a
  // stale/interrupted transition — e.g. the element unmounting mid-run some other way — must not
  // strand the toast on screen forever).
  useEffect(() => {
    if (!leaving) return;
    const fallback = setTimeout(() => onDismissRef.current(), 200);
    return () => clearTimeout(fallback);
  }, [leaving]);

  return (
    <div
      className={`toast toast--${toast.variant}`}
      data-leaving={leaving ? '' : undefined}
      role={toast.variant === 'error' ? 'alert' : 'status'}
      aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
      // MVP-381: `onFocus`/`onBlur` rather than `onFocusCapture` — React's synthetic focus events
      // bubble from the action and dismiss buttons inside, which is precisely the focus that must
      // hold the clock. `onMouseEnter`/`onMouseLeave` do not fire on a touchscreen, so hovering is
      // the mouse operator's pause and `documentHidden` plus a generous `durationMs` is the tablet
      // operator's — see `CUT_RECEIPT_MS`.
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
      // MVP-261: all three conditions are load-bearing. The keyframe-era handler could name the one
      // animation it cared about (`toast-leave`); `transitionend` has no such name, and BOTH the
      // enter and the exit transition opacity — so without the `leaving` guard every toast would
      // remove itself the instant it finished appearing. `e.target === e.currentTarget` keeps a
      // child's own transition (the action button's press scale, say) from bubbling up here and
      // closing the toast out from under the KJ who was reaching for it.
      onTransitionEnd={(e) => {
        if (leaving && e.propertyName === 'opacity' && e.target === e.currentTarget) onDismissRef.current();
      }}
    >
      <span className="toast__icon">
        <Icon name={ICONS[toast.variant]} size={16} />
      </span>
      <div className="toast__body">
        <p className="toast__title text-body">{toast.title}</p>
        {toast.description && <p className="toast__description text-meta">{toast.description}</p>}
      </div>
      {toast.action && (
        <Button
          variant="ghost"
          size="sm"
          className="toast__action"
          onClick={() => {
            toast.action!.onClick();
            requestClose();
          }}
        >
          {toast.action.label}
        </Button>
      )}
      <IconButton icon="close" label={dismissLabel} size="sm" onClick={requestClose} />
    </div>
  );
}
